import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { MicrophoneIcon, StopIcon } from '@heroicons/react/24/solid';
import { Visualizer } from 'react-sound-visualizer';
import { useAuth } from '../../../auth/AuthContext';
import { useTranslation } from 'react-i18next';
import { colors } from '../../../config/colors';
import { apiClient } from '../../../services/apiClient';

/**
 * Azure Speech Service Recorder Component
 * 
 * This component implements real-time speech-to-text transcription using Azure's
 * Speech Service WebSocket API. It provides:
 * 
 * - Real-time streaming transcription with partial and final results
 * - Multi-language support based on user preferences
 * - Proper audio processing and format conversion for Azure Speech Service
 * - WebSocket connection management with automatic cleanup
 * - Integration with the same transcript segment handling as other recorders
 * 
 * Requirements:
 * - AZURE_SPEECH_KEY environment variable set in backend
 * - AZURE_SPEECH_REGION environment variable (defaults to 'eastus')
 * - Backend endpoint /api/speech/config to provide Azure configuration
 * 
 * Usage:
 * - Default recorder when no ?recorder= parameter is specified
 * - Can be explicitly selected with ?recorder=azure URL parameter
 * - Supports the same props as other recorders (onTranscriptionUpdate, onError, etc.)
 */

const AzureSpeechRecorder = forwardRef(({ onTranscriptionUpdate, onError, onReset, onStop, helperText, additionalHelperText, priority = 'normal', transcriptSegments = [], selectedRole = '' }, ref) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const isRecordingRef = useRef(isRecording);
  useEffect(() => { isRecordingRef.current = isRecording; }, [isRecording]);
  const [audioStream, setAudioStream] = useState(null);
  
  const websocket = useRef(null);
  const audioContext = useRef(null);
  const mediaStreamSource = useRef(null);
  const processor = useRef(null);
  const texts = useRef([...transcriptSegments]);

  // Clean up any existing audio resources
  const cleanupAudioResources = useCallback(() => {
    if (processor.current) {
      processor.current.disconnect();
      processor.current = null;
    }
    if (mediaStreamSource.current) {
      mediaStreamSource.current.disconnect();
      mediaStreamSource.current = null;
    }
    if (audioContext.current && audioContext.current.state !== 'closed') {
      audioContext.current.close();
      audioContext.current = null;
    }
    if (audioStream) {
      audioStream.getTracks().forEach(track => track.stop());
      setAudioStream(null);
    }
  }, [audioStream]);

  // Convert Float32Array to Int16Array for Azure Speech Service
  const convertFloat32ToInt16 = useCallback((float32Array) => {
    const int16Array = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
      // Convert float32 to int16 with proper scaling
      const sample = Math.max(-1, Math.min(1, float32Array[i]));
      int16Array[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
    }
    return int16Array;
  }, []);


  const stopRecording = useCallback(() => {
    console.log('Stopping Azure Speech recording', isRecordingRef.current);

    if (isRecordingRef.current) {
      
      setIsRecording(false);
  
      // Stop the AudioWorklet processor
      if (processor.current?.port) {
        processor.current.port.postMessage({ type: 'stop' });
      }
  
      // Close WebSocket connection
      if (websocket.current) {
        // Send stop message
        if (websocket.current.readyState === WebSocket.OPEN) {
          websocket.current.send(JSON.stringify({
            type: 'stop'
          }));
        }
        websocket.current.close();
        websocket.current = null;
      }

      const updatedTexts = [...texts.current];
      updatedTexts.push('');
      texts.current = updatedTexts;
      onTranscriptionUpdate?.([...updatedTexts]);

      // Clean up audio resources
      cleanupAudioResources();
      onStop?.();
    }
  }, [cleanupAudioResources, onStop]);

  const startRecording = useCallback(async () => {
    // Initialize texts with current transcriptSegments
    texts.current = [...transcriptSegments];
    try {
      // Create audio context and processor first
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          sampleSize: 16,
          echoCancellation: true,
          noiseSuppression: true
        }
      });

      setAudioStream(stream);

      // Create and initialize audio context
      const myAudioContext = new AudioContext({
        sampleRate: 16000,
        latencyHint: 'interactive'
      });

      // Resume audio context if suspended (required by some browsers)
      if (myAudioContext.state === 'suspended') {
        await myAudioContext.resume();
      }


      // Create source from microphone stream
      const source = myAudioContext.createMediaStreamSource(stream);
      mediaStreamSource.current = source;

      // Load and register the AudioWorklet module
      await myAudioContext.audioWorklet.addModule('/worklets/azure-speech-worklet-processor.js');

      // Create AudioWorkletNode
      const workletNode = new AudioWorkletNode(myAudioContext, 'azure-speech-processor');
      processor.current = workletNode;

      // Connect the nodes
      source.connect(workletNode);
      workletNode.connect(myAudioContext.destination);

      audioContext.current = myAudioContext;
      // Determine language based on user preferences
      const language = user?.preferences?.language === 'fr' ? 'fr-FR' : 
                      user?.preferences?.language === 'de' ? 'de-DE' :
                      user?.preferences?.language === 'it' ? 'it-IT' : 'en-US';

      // Get the bearer token from localStorage
      const token = localStorage.getItem('access_token');

      if (!token) {
        onError?.(t('audioRecorder.errors.permissions'));
        return;
      }

      // Create WebSocket connection using backend proxy
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsBaseUrl = process.env.REACT_APP_API_BASE_URL
        ? process.env.REACT_APP_API_BASE_URL.replace(/^https?:/, wsProtocol)
        : `${wsProtocol}//${window.location.host}`;
      
      // Include token in WebSocket URL as query parameter
      const wsUrl = `${wsBaseUrl}/api/speech/ws?token=${encodeURIComponent(token)}`;
      websocket.current = new WebSocket(wsUrl);
      
      websocket.current.onopen = () => {
        console.log('Azure Speech WebSocket connected via backend, role:', selectedRole);
        
        // Send initialization message with language and consultation sub-role (for keyterms)
        websocket.current.send(JSON.stringify({
          type: 'start',
          language: language,
          role: selectedRole || ''
        }));

        // Set up audio data handling from worklet
        workletNode.port.onmessage = (event) => {
          if (event.data.type === 'audio' && websocket.current?.readyState === WebSocket.OPEN) {
            websocket.current.send(event.data.data);
          }
        };

        setIsRecording(true);
      };

      websocket.current.onclose = (event) => {
        console.log('Azure Speech WebSocket closed:', event.code, event.reason);
        cleanupAudioResources();
      };

      websocket.current.onerror = (error) => {
        console.error('Azure Speech WebSocket error:', error);
        onError?.(t('audioRecorder.errors.connection'));
        stopRecording();
      };

      websocket.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          //console.log('Azure Speech message:', data);

          if (data.type === 'Results' || data.type === 'Partial') {
            const transcript = data.text;
            const isFinal = data.type === 'Results';
            
            if (transcript) {
              //console.log('Transcript:', transcript, 'isFinal:', isFinal);
              
              // Handle transcription similar to WebSpeechRecorder
              const updatedTexts = [...texts.current];
              
              if (isFinal) {
                // Remove any existing partial result
                if (updatedTexts.length > 0 && updatedTexts[updatedTexts.length - 1] !== '') {
                  updatedTexts.pop();
                }
                if (updatedTexts.length > 0 && updatedTexts[updatedTexts.length - 1] === '') {
                  updatedTexts.pop();
                }
                updatedTexts.push(transcript.trim());
                updatedTexts.push(''); // Add empty string for next partial result
                texts.current = updatedTexts;
                onTranscriptionUpdate?.([...updatedTexts]);
              } else {
                // Handle partial results
                if (updatedTexts.length > 0 && updatedTexts[updatedTexts.length - 1] !== '') {
                  updatedTexts.pop();
                }
                updatedTexts.push(transcript);
                texts.current = updatedTexts;
                onTranscriptionUpdate?.([...updatedTexts]);
              }
            }
          } else if (data.error) {
            console.error('Received error:', data.error);
            onError?.(data.error);
          }
        } catch (error) {
          console.error('Error handling Azure Speech message:', error);
        }
      };

    } catch (error) {
      console.error('Error starting Azure Speech recording:', error);
      onError?.(t('audioRecorder.errors.initialization'));
      stopRecording();
    }
  }, [cleanupAudioResources, onError, t, user?.preferences?.language, onTranscriptionUpdate, transcriptSegments, stopRecording, selectedRole]);

  const handleToggleRecording = useCallback((e) => {
    // Prevent form submission
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, stopRecording, startRecording]);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'x') {
        // Check if we're inside a modal (higher priority context)
        const isInModal = document.querySelector('[role="dialog"]') !== null;
        const isInFeedbackModal = document.querySelector('[data-feedback-modal]') !== null;
        
        // If this recorder has 'high' priority (feedback modal) or if no modal is open
        const shouldHandle = priority === 'high' || (!isInModal && priority === 'normal');
        
        // Additional check: if we're in a feedback modal, only high priority recorders should respond
        if (isInFeedbackModal && priority !== 'high') {
          console.log(`Azure Speech Recorder (priority: ${priority}) - Skipping, feedback modal is open`);
          return;
        }
        
        if (shouldHandle) {
          console.log(`Azure Speech Recorder (priority: ${priority}) - Handling shortcut`);
          event.preventDefault();
          event.stopPropagation();
          handleToggleRecording();
        } else {
          console.log(`Azure Speech Recorder (priority: ${priority}) - Not handling, modal is open`);
        }
      }
    };

    // Use capture phase for high priority recorders to intercept first
    const useCapture = priority === 'high';
    window.addEventListener('keydown', handleKeyPress, useCapture);
    return () => {
      window.removeEventListener('keydown', handleKeyPress, useCapture);
    };
  }, [handleToggleRecording, priority]);

  useEffect(() => {
    if (onReset) {
      return () => {
        if (isRecording) {
          stopRecording();
        }
      };
    }
  }, [onReset, isRecording, stopRecording]);

  useImperativeHandle(ref, () => ({
    stopRecording
  }));

  return (
    <div className="px-6 py-4">
      {/* Main Horizontal Controls */}
      <div className="flex items-center justify-between space-x-6 mb-4">
        {/* Left side - Audio Visualizer */}
        <div className="flex-1">
          {isRecording && audioStream ? (
            <Visualizer
              audio={audioStream}
              autoStart={true}
              strokeColor="#c5d6f8"
              mode="continuous" 
              rectWidth={3} 
              slices={50}
              heightNorm={1}
            >
              {({ canvasRef }) => (
                <canvas 
                  ref={canvasRef} 
                  style={{ height: '80px', width: '100%' }}
                  className="rounded-lg"
                />
              )}
            </Visualizer>
          ) : (
            <div className="flex flex-col items-center justify-center h-[80px] bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-sm text-gray-500">
                {isRecording ? t('audioRecorder.recording') : helperText || t('audioRecorder.clickToStart')}
                <span className="ml-2 text-xs text-gray-400">
                  {t('audioRecorder.shortcut')}
                </span>
              </p>
              {!isRecording && additionalHelperText && (
                <p className="text-xs text-gray-400 italic mt-1 px-4 text-center">
                  {additionalHelperText}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Right side - Control Button */}
        <div className="flex items-center">
          {/* Record/Stop Button */}
          <button
            type="button"
            onClick={handleToggleRecording}
            className={`flex items-center justify-center w-16 h-16 rounded-full transition-all duration-200 focus:outline-none focus:ring-4 ${
              isRecording 
                ? 'bg-red-300 hover:bg-red-400 focus:ring-red-200' 
                : 'hover:bg-teal-400 focus:ring-teal-200'
            }`}
            style={!isRecording ? {
              backgroundColor: colors.primary.selected
            } : undefined}
            aria-label={isRecording ? t('audioRecorder.stopRecording') : t('audioRecorder.startRecording')}
          >
            {isRecording ? (
              <StopIcon className="w-8 h-8 text-white" />
            ) : (
              <MicrophoneIcon className="w-8 h-8 text-white" />
            )}
          </button>
        </div>
      </div>

      {/* Recording Status Bar */}
      {isRecording && (
        <div className="flex items-center justify-center space-x-2 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
            <span className="font-medium text-red-600">{t('audioRecorder.recordingStatus')}</span>
          </div>
        </div>
      )}
    </div>
  );
});

export default AzureSpeechRecorder; 